/* eslint-disable @typescript-eslint/no-explicit-any */
import { ref, computed } from "vue";
import type { Ref } from "vue";
import type { CalendarRequestPayload } from "@/types/domain";
import { getAvailabilityCalendar } from "@/services/naaviApi/internalService";
import { toStartOfDayIso } from "@/helpers/dateHelper";
import {
  normalizeCalendarResponse,
  cleanCalendarParams,
} from "@/helpers/availabilityCalendarHelper";
import { resolveCalendarShiftDate } from "@/helpers/calendarNavigationHelper";
import { ensureCenterVariantKeys } from "@/helpers/bookingOptionHelper";
import {
  normalizeBookingKey,
  getDoctorKey,
  hasCalendarSchedule,
} from "@/helpers/doctorSelectionHelper";
import {
  applyCalendarResultToDoctor,
  applyCalendarErrorToDoctor,
} from "@/helpers/availabilityCalendarUpdateHelper";

const SEARCH_TYPE_FULL_DAY = 2;
const DEFAULT_CALENDAR_DAYS = 4;

export function useDoctorSchedule(doctor: Ref<any>) {
  const doctorSchedule = ref<any>(null);
  const selectedBookingKey = ref<string | null>(null);

  const hasDoctorSchedule = computed(() => {
    const schedule = doctorSchedule.value;
    if (!schedule) return false;
    const hasBookingOptions =
      Array.isArray(schedule.bookingOptions) &&
      schedule.bookingOptions.length > 0;
    const hasAddresses =
      Array.isArray(schedule.addresses) && schedule.addresses.length > 0;
    return hasBookingOptions || hasAddresses;
  });

  const activeScheduleLoading = computed(() => {
    const schedule = doctorSchedule.value;
    if (!schedule) return false;
    const optionsList = schedule.bookingOptions ?? [];
    if (!optionsList.length) return false;

    const normalized = selectedBookingKey.value;
    const option = normalized
      ? optionsList.find((item: any) => String(item?.key) === normalized) ??
        optionsList[0]
      : optionsList[0];
    return option?.scheduleLoading === true;
  });

  const mapSelectedBookingKeys = computed(() => {
    const bookingKey = selectedBookingKey.value;
    if (!bookingKey) return {};

    const doctorUser = doctor.value?.user ?? null;
    if (!doctorUser) return {};

    const doctorKey = getDoctorKey(doctorUser);
    if (!doctorKey) return {};

    return { [doctorKey]: bookingKey };
  });

  function collectBookingOptions(scheduleSource: any, doctorUser: any): any[] {
    const scheduleOptions = scheduleSource?.bookingOptions ?? [];
    const doctorOptions = doctorUser?.bookingOptions ?? [];
    const combined = [...scheduleOptions, ...doctorOptions];

    const unique: any[] = [];
    const seen = new Set<string>();

    combined.forEach((option: any) => {
      if (!option) return;
      const key = normalizeBookingKey(option);
      if (!key || seen.has(key)) return;
      seen.add(key);
      unique.push(option);
    });

    return unique;
  }

  function resolveInitialBookingKey(
    scheduleSource: any,
    doctorUser: any,
    currentKey: string | null = null,
  ): string | null {
    const optionsList = collectBookingOptions(scheduleSource, doctorUser);
    if (!optionsList.length) return null;

    const normalizedCurrent = normalizeBookingKey(currentKey);
    if (
      normalizedCurrent &&
      optionsList.some(
        (option: any) => normalizeBookingKey(option) === normalizedCurrent,
      )
    ) {
      return normalizedCurrent;
    }

    const preferred = optionsList.find(
      (option: any) => option.isDefault === true || option.default === true,
    );

    return normalizeBookingKey(preferred ?? optionsList[0]);
  }

  function applyScheduleFromProfile(schedule: any, doctorUser: any): void {
    const nextBookingKey = resolveInitialBookingKey(
      schedule,
      doctorUser,
      selectedBookingKey.value,
    );
    selectedBookingKey.value = nextBookingKey;
    doctorSchedule.value = schedule;
  }

  function isScheduleCurrent(doctorRef: any, option: any): boolean {
    if (!option) return false;
    const schedule = option.schedule ?? [];
    if (!schedule.length) return false;

    const baseDateIso = doctorRef?.baseDateIso;
    const scheduleStart = schedule[0]?.date;

    if (!baseDateIso || !scheduleStart) return true;
    return baseDateIso.indexOf(scheduleStart) === 0;
  }

  function syncDoctorUser(updatedSchedule: any): void {
    if (doctor.value?.user) {
      doctor.value.user.bookingOptions = updatedSchedule.bookingOptions;
      doctor.value.user.totalDatesMax = updatedSchedule.totalDatesMax;
      doctor.value.user.totalTimesMax = updatedSchedule.totalTimesMax;
      doctor.value.user.baseDateIso = updatedSchedule.baseDateIso;
      doctor.value.user.scheduleVersion = updatedSchedule.scheduleVersion;
    }
  }

  async function fetchScheduleCalendar(payload: {
    bookingKey: string | null;
    date: Date | string | null;
    days: number;
    force?: boolean;
    direction?: "prev" | "next" | null;
  }): Promise<void> {
    const schedule = doctorSchedule.value;
    if (!schedule) return;

    const normalizedKey =
      normalizeBookingKey(payload.bookingKey) ??
      normalizeBookingKey(selectedBookingKey.value) ??
      normalizeBookingKey(schedule.bookingOptions?.[0]?.key);

    if (!normalizedKey) return;

    const bookingOptions = [...(schedule.bookingOptions ?? [])];
    const optionIndex = bookingOptions.findIndex(
      (option: any) => String(option?.key) === normalizedKey,
    );
    if (optionIndex === -1) return;

    const originalOption = bookingOptions[optionIndex];
    const previousSchedule = originalOption.schedule ?? [];

    if (!payload.force && originalOption.scheduleLoaded) return;
    if (originalOption.scheduleLoading) return;

    const pendingOption = {
      ...originalOption,
      scheduleLoading: true,
      scheduleError: null,
    };
    const pendingOptions = bookingOptions.map((option: any, index: number) =>
      index === optionIndex ? pendingOption : option,
    );
    const pendingSchedule = { ...schedule, bookingOptions: pendingOptions };
    doctorSchedule.value = pendingSchedule;

    if (doctor.value?.user) {
      doctor.value.user.bookingOptions = pendingOptions;
    }

    const base = payload.date
      ? new Date(payload.date as string)
      : schedule.baseDateIso
      ? new Date(schedule.baseDateIso)
      : new Date();
    if (!Number.isNaN(base.getTime())) {
      base.setHours(0, 0, 0, 0);
    }

    const params = cleanCalendarParams({
      date: toStartOfDayIso(base),
      searchType: SEARCH_TYPE_FULL_DAY,
      concept: 0,
      days: payload.days ?? DEFAULT_CALENDAR_DAYS,
    });
    const tenantKey = schedule.tenantXId ?? schedule.tenantId;

    if (!tenantKey) {
      const errorResult = applyCalendarErrorToDoctor(
        pendingSchedule,
        optionIndex,
        "Calendar unavailable",
      );
      if (errorResult) {
        doctorSchedule.value = errorResult.doctor;
        syncDoctorUser(errorResult.doctor);
      }
      return;
    }

    try {
      const response = await getAvailabilityCalendar(
        tenantKey,
        schedule.resourceId,
        pendingOption.bookingTypeId,
        params,
      );

      const calendarResult = normalizeCalendarResponse({
        response,
        previousSchedule,
        merge: !payload.force && !payload.direction,
        fallbackBaseDate: base,
        fallbackDays: params.days as number,
        fallbackBaseDateIso: params.date as string,
        currentPriceLocalized: pendingOption.priceLocalized,
        currentPriceLabel: pendingOption.price,
      });

      const updateResult = applyCalendarResultToDoctor({
        doctorEntry: pendingSchedule as any,
        optionIndex,
        calendarResult: calendarResult as any,
        fallbackBaseDateIso: params.date as string,
        ensureCenterVariantKeys: ensureCenterVariantKeys as any,
      });

      if (updateResult) {
        doctorSchedule.value = updateResult.doctor;
        syncDoctorUser(updateResult.doctor);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Calendar unavailable";
      const errorResult = applyCalendarErrorToDoctor(
        pendingSchedule,
        optionIndex,
        errorMessage,
      );
      if (errorResult) {
        doctorSchedule.value = errorResult.doctor;
        syncDoctorUser(errorResult.doctor);
      }
    }
  }

  function handleBookingOptionSelection(
    bookingKey: string,
    context?: { source?: string },
  ): void {
    const normalized = normalizeBookingKey(bookingKey);
    selectedBookingKey.value = normalized;

    if (!normalized) return;

    const optionsList = doctorSchedule.value?.bookingOptions ?? [];
    const selectedOption =
      optionsList.find((option: any) => String(option?.key) === normalized) ??
      optionsList[0];
    const isInit = context?.source === "init";
    const baseDateIso = doctorSchedule.value?.baseDateIso ?? null;

    if (
      isInit &&
      selectedOption?.scheduleLoaded &&
      hasCalendarSchedule(selectedOption) &&
      isScheduleCurrent(doctorSchedule.value, selectedOption)
    ) {
      return;
    }

    fetchScheduleCalendar({
      bookingKey: normalized,
      date: baseDateIso,
      days: DEFAULT_CALENDAR_DAYS,
      force: true,
    });
  }

  async function handleScheduleMoreDates(
    payload: CalendarRequestPayload,
  ): Promise<void> {
    const bookingKey = normalizeBookingKey(
      payload.bookingKey ?? selectedBookingKey.value,
    );
    if (!bookingKey) return;

    const schedule = doctorSchedule.value;
    if (!schedule?.bookingOptions) return;

    const option =
      schedule.bookingOptions.find(
        (item: any) => String(item?.key) === bookingKey,
      ) ?? schedule.bookingOptions[0];
    if (!option) return;

    const optionSchedule = option.schedule ?? [];
    if (!optionSchedule.length) {
      await fetchScheduleCalendar({
        bookingKey,
        date: schedule.baseDateIso ?? null,
        days: DEFAULT_CALENDAR_DAYS,
        force: true,
      });
      return;
    }

    const targetDate =
      payload.targetDate ||
      resolveCalendarShiftDate({
        direction: payload.direction ?? null,
        schedule: optionSchedule,
        firstDate: payload.firstDate ?? null,
        lastDate: payload.lastDate ?? null,
        days: DEFAULT_CALENDAR_DAYS,
      });

    if (!targetDate) return;

    const targetDateIso = toStartOfDayIso(targetDate);
    if (schedule.baseDateIso === targetDateIso) return;

    await fetchScheduleCalendar({
      bookingKey,
      date: targetDate,
      days: DEFAULT_CALENDAR_DAYS,
      force: true,
      direction: payload.direction,
    });
  }

  async function handleScheduleCalendar(
    payload: CalendarRequestPayload,
  ): Promise<void> {
    await fetchScheduleCalendar({
      bookingKey: payload.bookingKey ?? selectedBookingKey.value,
      date: payload.date ?? doctorSchedule.value?.baseDateIso ?? null,
      days: payload.days ?? DEFAULT_CALENDAR_DAYS,
      force: true,
    });
  }

  return {
    doctorSchedule,
    selectedBookingKey,
    hasDoctorSchedule,
    activeScheduleLoading,
    mapSelectedBookingKeys,
    applyScheduleFromProfile,
    handleBookingOptionSelection,
    handleScheduleMoreDates,
    handleScheduleCalendar,
  };
}
